import { ReactNode, useMemo } from "react"
import { GetShip } from "../../App/Context"
import { IShipUser } from "../../Types"
import { deps, many, useDeps, useDataStore, type DepsIds } from "../../Data"
import { SchemaEntities } from "../../Types/Generated/LogbookModel/Schema"
import { ShipDataUsersViewProps } from "./Params"
import { ShipUsersData, ShipUsersDataContext } from "./UsersDataContext"
import { groupShipUsers } from "./shipUserGroups"
import { createStoreShipUserWriters } from "./writers"

// The centralized-store crew slice for the ship_users tab: the ship's ShipUser roster,
// owner-scoped by the `Ship -> ShipUser` schema edge (`shipId`). The roster has no list
// route of its own -- it rides the `ships.get` bundle via the ShipUser connection's
// `providedBy: "Ship"`, so `useDeps` fetches it through Ship's fetch and keeps it live via
// `Subscription.ShipUser` (soft-delete aware). (issue #1186)
export const ShipUsersDeps = deps(SchemaEntities.Ship, {
  users: many(SchemaEntities.ShipUser),
});

// New (flag-on) crew acquisition variant: reads the roster from the centralized store via
// `useDeps` and provides store-backed optimistic writers (create reconciles, update upserts
// optimistically, delete removes optimistically then rolls back on failure). No `<Loading/>`
// spinner -- the store yields a resolved (possibly empty) array immediately from its
// seed/fetch, so there is never "required data still being fetched" to wait on.
export function UsersDataView({ children }: ShipDataUsersViewProps & { children?: ReactNode }) {
  const store = useDataStore();
  const ship = GetShip();
  // Annotated with the declaration's own id contract so the key is checked against its
  // FK fields (a wide `BoundIds` would accept anything).
  const ids: DepsIds<typeof ShipUsersDeps.relationships> = { shipId: ship.id };
  const { users } = useDeps(ShipUsersDeps, ids);

  // `useDeps` returns readonly snapshots; the roster contract is a mutable array. Cast
  // without copying to preserve referential stability -- consumers never mutate it.
  const shipUsers = users as IShipUser[];
  const groups = useMemo(() => groupShipUsers(shipUsers), [shipUsers]);
  const writers = useMemo(
    () => createStoreShipUserWriters(store, { shipId: ship.id }),
    [store, ship.id],
  );

  const value: ShipUsersData = {
    shipUsers,
    leveledShipUsers: groups.leveled,
    unleveledShipUsers: groups.unleveled,
    writers,
  };
  return <ShipUsersDataContext.Provider value={value}>{children}</ShipUsersDataContext.Provider>;
}
